import R from 'ramda';
import * as steps from 'common/steps';
import { getStep } from 'common/selectors';
import { getFields } from 'common/stepReducerUtil';

import {
  EMBARGO_PERIOD_SIX_MONTHS,
  EMBARGO_PERIOD_ONE_YEAR,
  EMBARGO_PERIOD_TWO_YEARS,
} from 'options/optionsReducer';

const notNilOrEmpty = R.complement(R.anyPass([R.isEmpty, R.isNil]));
const pickNonEmptyFields = R.pickBy(notNilOrEmpty);
const evolveAndRemoveEmpty = spec => R.pipe(R.evolve(spec), pickNonEmptyFields);

const daysFromNow = days => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date;
};

const mapEmbargoPeriodToDate = embargoPeriod => {
  switch (embargoPeriod) {
    case EMBARGO_PERIOD_SIX_MONTHS:
      return daysFromNow(180);
    case EMBARGO_PERIOD_ONE_YEAR:
      return daysFromNow(360);
    case EMBARGO_PERIOD_TWO_YEARS:
      return daysFromNow(720);
    default:
      return null;
  }
};

const mapOptions = R.pipe(
  getStep(steps.OPTIONS),
  getFields,
  R.applySpec({
    optIns: R.pipe(R.prop('acceptedOptIns'), R.map(optInId => ({ optInId }))),
    delayReleaseDate: R.pipe(R.prop('embargoPeriod'), mapEmbargoPeriodToDate),
  }),
  pickNonEmptyFields,
);

const mapStudent = evolveAndRemoveEmpty({
  citizenShip: R.path(['code']),
  permanentAddress: evolveAndRemoveEmpty({
    administrativeArea: R.prop('code'),
    country: R.prop('code'),
  }),
});

const mapContactInfo = R.pipe(
  getStep(steps.CONTACT_INFO),
  getFields,
  R.applySpec({
    student: mapStudent,
  }),
);

const mapCategories = fields =>
  R.pipe(R.filter(R.identity), R.map(R.pick(['categoryId'])))([
    fields.primaryCategory,
    fields.secondaryCategory,
    fields.tertiaryCategory,
  ]);

const mapCommitteeMember = R.curry(
  (role, { firstName, lastName }) =>
    !R.isEmpty(firstName) && !R.isEmpty(lastName)
      ? { firstName, lastName, role }
      : null,
);

const mapCommitteeMembers = fields =>
  R.filter(R.identity, [
    mapCommitteeMember('ADVISOR', fields.advisor),
    mapCommitteeMember('SUPERVISOR', fields.supervisor),
    mapCommitteeMember('COMMITTEE_CHAIR', fields.committeeChair),
    ...R.map(mapCommitteeMember('COMMITTEE_MEMBER'), fields.committeeMembers),
  ]);

const mapDissertationInfo = R.pipe(
  getStep(steps.DISSERTATION_INFO),
  getFields,
  R.converge(R.merge, [
    evolveAndRemoveEmpty({
      language: R.prop('code'),
      degreeAwarded: R.pick(['degreeId']),
      department: R.pick(['departmentId']),
    }),
    R.applySpec({
      categories: mapCategories,
      committeeMembers: mapCommitteeMembers,
      licenseType: R.pipe(R.prop('licenseType'), R.omit(['licenseTypeName'])),
    }),
  ]),
  R.omit([
    'advisor',
    'supervisor',
    'committeeChair',
    'primaryCategory',
    'secondaryCategory',
    'tertiaryCategory',
  ]),
);

const mapSubmissionData = state =>
  R.mergeAll([
    mapOptions(state),
    mapContactInfo(state),
    mapDissertationInfo(state),
  ]);

export default mapSubmissionData;



// WEBPACK FOOTER //
// ./src/submission/mapSubmissionData.js